import { Button, Modal } from "@redelair-sa/intiza-ui";
import { Announcement } from "../../../../services/AnnouncementService";
import { TranslationService } from "../../../../services/TranslationService";
import { formatAnnouncementCounter } from "./announcementFormat";
import "./announcements.css";

/** Por encima de las ventanas flotantes (1000) y debajo de ModalService (3000). */
const ANNOUNCEMENT_MODAL_Z_INDEX = 2990;

export type AnnouncementModalProps = {
  announcement: Announcement;
  open: boolean;
  /** X, Esc, clic afuera y "Ver más tarde": el comunicado sigue pendiente. */
  onClose: () => void;
  /** Sin `position` (o con un solo comunicado) no se muestra el contador. */
  position?: { index: number; total: number };
  onPrev?: () => void;
  onNext?: () => void;
  showDontShowAgain?: boolean;
  dontShowAgain?: boolean;
  onDontShowAgainChange?: (checked: boolean) => void;
  onCtaClick?: () => void;
};

/** Presentación del comunicado: sin estado ni llamadas al backend. */
const AnnouncementModal = ({
  announcement,
  open,
  onClose,
  position,
  onPrev,
  onNext,
  showDontShowAgain = false,
  dontShowAgain = false,
  onDontShowAgainChange,
  onCtaClick
}: AnnouncementModalProps) => {
  const { translate } = TranslationService;
  const showNavigation = position !== undefined && position.total > 1;

  const handleCtaClick = () => {
    onCtaClick?.();
    if (announcement.link) {
      window.open(announcement.link, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={announcement.title}
      headerVariant="plain"
      width={560}
      zIndex={ANNOUNCEMENT_MODAL_Z_INDEX}
      closeAriaLabel={translate.Close}
      footer={
        <>
          {showDontShowAgain && (
            <label className="announcement-modal__dont-show">
              <input
                type="checkbox"
                checked={dontShowAgain}
                disabled={dontShowAgain}
                onChange={(event) =>
                  onDontShowAgainChange?.(event.target.checked)
                }
              />
              {translate.AnnouncementDontShowAgain}
            </label>
          )}
          {showNavigation && (
            <div className="announcement-modal__nav">
              <button
                type="button"
                className="announcement-modal__arrow"
                aria-label={translate.Previous}
                onClick={onPrev}
              >
                <i className="fa-solid fa-chevron-left" aria-hidden="true" />
              </button>
              <span>
                {formatAnnouncementCounter(position.index + 1, position.total)}
              </span>
              <button
                type="button"
                className="announcement-modal__arrow"
                aria-label={translate.Next}
                onClick={onNext}
              >
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
              </button>
            </div>
          )}
          <Button variant="link" size="sm" onClick={onClose}>
            {translate.AnnouncementViewLater}
          </Button>
        </>
      }
    >
      {announcement.imageUrl && (
        <div className="announcement-modal__media">
          <img src={announcement.imageUrl} alt={announcement.title} />
        </div>
      )}
      <p className="announcement-modal__description">
        {announcement.description}
      </p>
      {announcement.link && (
        <Button
          className="announcement-modal__cta"
          size="sm"
          endIcon={
            <i
              className="fa-solid fa-arrow-up-right-from-square"
              aria-hidden="true"
            />
          }
          onClick={handleCtaClick}
        >
          {translate.AnnouncementMoreInfo}
        </Button>
      )}
    </Modal>
  );
};

export default AnnouncementModal;
